import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    confirmPackageImage,
    createProductPackage,
    generatePackageCopy,
    presignPackageImage,
    referenceQuote,
    updateProductPackage,
} from "./functions";

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const nextMonthInputValue = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString().slice(0, 10);
};

export const slugifyPackageName = (value) => (
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
);

const toInputDate = (value, fallback) => {
    if (!value) {
        return fallback;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return fallback;
    }
    return date.toISOString().slice(0, 10);
};

const PACKAGE_REFERENCE_PRIORITY = "none";
const MAX_PACKAGE_ITEMS = 8;

const defaultItem = () => ({
    gradingType: "premium",
    numberOfCards: 10,
    autographEvaluation: false,
});

export const normalizeFormItems = (productPackage = null) => {
    if (Array.isArray(productPackage?.items) && productPackage.items.length) {
        return productPackage.items.map((item) => ({
            gradingType: item.gradingType || "premium",
            numberOfCards: item.numberOfCards || 1,
            autographEvaluation: Boolean(item.autographEvaluation),
        }));
    }
    if (productPackage?.gradingType || productPackage?.numberOfCards) {
        return [{
            gradingType: productPackage.gradingType || "premium",
            numberOfCards: productPackage.numberOfCards || 1,
            autographEvaluation: Boolean(productPackage.autographEvaluation),
        }];
    }
    return [defaultItem()];
};

const buildInitialForm = (productPackage = null) => ({
    internalName: productPackage?.internalName || "",
    slug: productPackage?.slug || "",
    title: productPackage?.title || "",
    subtitle: productPackage?.subtitle || "",
    items: normalizeFormItems(productPackage),
    // Prioridade não é exposta no formulário: prazo promo é definido à mão.
    priority: PACKAGE_REFERENCE_PRIORITY,
    promoPriceCents: productPackage?.promoPriceCents ?? 90000,
    promoDeadlineBusinessDays: productPackage?.promoDeadlineBusinessDays || 10,
    startsAt: toInputDate(productPackage?.startsAt, todayInputValue()),
    endsAt: toInputDate(productPackage?.endsAt, nextMonthInputValue()),
    isActive: productPackage?.isActive ?? true,
    imageUrl: productPackage?.imageUrl || "",
});

const inputDateToIso = (value, time, fieldLabel) => {
    if (!value) {
        return { type: "error", message: `Informe uma data de ${fieldLabel} válida.` };
    }

    const date = new Date(`${value}${time}`);
    if (Number.isNaN(date.getTime())) {
        return { type: "error", message: `Informe uma data de ${fieldLabel} válida.` };
    }

    return { type: "success", iso: date.toISOString() };
};

const buildPayload = (form) => {
    const startsAt = inputDateToIso(form.startsAt, "T00:00:00.000", "início");
    if (startsAt.type !== "success") {
        return startsAt;
    }

    const endsAt = inputDateToIso(form.endsAt, "T23:59:59.999", "fim");
    if (endsAt.type !== "success") {
        return endsAt;
    }

    const items = (form.items || []).map((item) => ({
        gradingType: item.gradingType,
        numberOfCards: Number(item.numberOfCards),
        autographEvaluation: Boolean(item.autographEvaluation),
    }));

    return {
        internalName: form.internalName,
        slug: form.slug,
        title: form.title,
        subtitle: form.subtitle,
        items,
        priority: PACKAGE_REFERENCE_PRIORITY,
        promoPriceCents: Number(form.promoPriceCents),
        promoDeadlineBusinessDays: Number(form.promoDeadlineBusinessDays),
        isActive: Boolean(form.isActive),
        startsAt: startsAt.iso,
        endsAt: endsAt.iso,
    };
};

const packageIdentity = (productPackage) => (
    productPackage?._id || productPackage?.id || null
);

export const usePackageEditor = ({ token, productPackage = null } = {}) => {
    const [form, setForm] = useState(() => buildInitialForm(productPackage));
    const [slugTouched, setSlugTouched] = useState(Boolean(productPackage?.slug));
    const [quote, setQuote] = useState(null);
    const [referenceLoading, setReferenceLoading] = useState(false);
    const [generatingCopy, setGeneratingCopy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [error, setError] = useState("");
    const [copyStatus, setCopyStatus] = useState("");
    const generatingCopyRef = useRef(false);
    const packageId = packageIdentity(productPackage);

    useEffect(() => {
        setForm(buildInitialForm(productPackage));
        setSlugTouched(Boolean(productPackage?.slug));
        setQuote(null);
        setImageFile(null);
        setError("");
        setCopyStatus("");
        generatingCopyRef.current = false;
        setGeneratingCopy(false);
        // Só reinicia ao trocar o pacote (id), não a cada novo objeto com o mesmo id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId]);

    const updateField = useCallback((field, value) => {
        setForm((current) => {
            const next = { ...current, [field]: value };
            if (field === "internalName" && !slugTouched) {
                next.slug = slugifyPackageName(value);
            }
            if (field === "slug") {
                next.slug = slugifyPackageName(value);
            }
            return next;
        });
        if (field === "slug") {
            setSlugTouched(true);
        }
    }, [slugTouched]);

    const updateItem = useCallback((index, field, value) => {
        setForm((current) => {
            const items = current.items.map((item, itemIndex) => {
                if (itemIndex !== index) {
                    return item;
                }
                const nextItem = { ...item, [field]: value };
                if (field === "gradingType" && value === "premium") {
                    nextItem.autographEvaluation = false;
                }
                return nextItem;
            });
            return { ...current, items };
        });
    }, []);

    const addItem = useCallback(() => {
        setForm((current) => {
            if ((current.items || []).length >= MAX_PACKAGE_ITEMS) {
                return current;
            }
            return {
                ...current,
                items: [
                    ...current.items,
                    {
                        gradingType: "no_subgrades",
                        numberOfCards: 1,
                        autographEvaluation: false,
                    },
                ],
            };
        });
    }, []);

    const removeItem = useCallback((index) => {
        setForm((current) => {
            if ((current.items || []).length <= 1) {
                return current;
            }
            return {
                ...current,
                items: current.items.filter((_, itemIndex) => itemIndex !== index),
            };
        });
    }, []);

    const referencePayload = useMemo(() => ({
        items: (form.items || []).map((item) => ({
            gradingType: item.gradingType,
            numberOfCards: Number(item.numberOfCards),
            autographEvaluation: Boolean(item.autographEvaluation),
        })),
        priority: PACKAGE_REFERENCE_PRIORITY,
    }), [form.items]);

    const itemsReady = useMemo(
        () => referencePayload.items.every((item) => (
            item.gradingType
            && Number.isFinite(item.numberOfCards)
            && item.numberOfCards >= 1
        )),
        [referencePayload.items]
    );

    useEffect(() => {
        if (!token || !itemsReady || !referencePayload.items.length) {
            return undefined;
        }

        let cancelled = false;
        const timeout = setTimeout(async () => {
            setReferenceLoading(true);
            const data = await referenceQuote(token, referencePayload);
            if (cancelled) {
                return;
            }
            if (data?.type === "success") {
                setQuote(data.data);
            } else {
                setError(data?.message || "Erro ao obter referência do pacote.");
            }
            setReferenceLoading(false);
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [itemsReady, referencePayload, token]);

    const savings = useMemo(() => {
        const promo = Number(form.promoPriceCents);
        const full = Number(quote?.selected?.priceFullCents ?? quote?.priceFullCents);
        const withQty = Number(
            quote?.selected?.priceWithQtyDiscountCents ?? quote?.priceWithQtyDiscountCents
        );
        const baseline = Number.isFinite(full) ? full : (Number.isFinite(withQty) ? withQty : null);
        if (!Number.isFinite(promo) || baseline == null || baseline <= promo) {
            return null;
        }
        return {
            baselineCents: baseline,
            savedCents: baseline - promo,
            savedPercent: Math.round(((baseline - promo) / baseline) * 100),
        };
    }, [form.promoPriceCents, quote]);

    const canGenerateCopy = useMemo(() => {
        const promo = Number(form.promoPriceCents);
        const deadline = Number(form.promoDeadlineBusinessDays);
        return Boolean(
            itemsReady
            && Number.isFinite(promo) && promo >= 0
            && Number.isFinite(deadline) && deadline >= 1
            && quote
        );
    }, [
        form.promoDeadlineBusinessDays,
        form.promoPriceCents,
        itemsReady,
        quote,
    ]);

    const generateCopyPayload = useMemo(() => ({
        items: referencePayload.items,
        priority: PACKAGE_REFERENCE_PRIORITY,
        internalName: form.internalName || undefined,
        promoPriceCents: Number(form.promoPriceCents),
        promoDeadlineBusinessDays: Number(form.promoDeadlineBusinessDays),
        priceFullCents: quote?.selected?.priceFullCents ?? quote?.priceFullCents,
        priceWithQtyDiscountCents: quote?.selected?.priceWithQtyDiscountCents ?? quote?.priceWithQtyDiscountCents,
    }), [
        form.internalName,
        form.promoDeadlineBusinessDays,
        form.promoPriceCents,
        quote,
        referencePayload.items,
    ]);

    const generateCopy = useCallback(async () => {
        if (generatingCopyRef.current) {
            return {
                type: "error",
                message: "Já existe uma geração de copy em andamento.",
            };
        }

        if (!canGenerateCopy) {
            const result = {
                type: "error",
                message: "Preencha oferta, preço, prazo e aguarde a referência antes de gerar a copy.",
            };
            setError(result.message);
            setCopyStatus("");
            return result;
        }

        generatingCopyRef.current = true;
        setGeneratingCopy(true);
        setError("");
        setCopyStatus("Gerando copy com IA...");
        try {
            const data = await generatePackageCopy(token, generateCopyPayload);
            if (data?.type === "success") {
                const title = String(data.data?.title || "").trim();
                const subtitle = String(data.data?.subtitle || "").trim();
                if (!title || !subtitle) {
                    const result = {
                        type: "error",
                        message: "A IA respondeu sem título/subtítulo válidos. Tente de novo.",
                    };
                    setError(result.message);
                    setCopyStatus("");
                    return result;
                }
                setForm((current) => ({
                    ...current,
                    title,
                    subtitle,
                }));
                setCopyStatus("Copy gerada — revise título e subtítulo abaixo.");
                return data;
            }
            const message = data?.message || "Erro ao gerar IA.";
            setError(message);
            setCopyStatus("");
            return data;
        } catch (error) {
            const result = {
                type: "error",
                message: "Erro ao gerar copy do pacote!",
            };
            setError(result.message);
            setCopyStatus("");
            return result;
        } finally {
            generatingCopyRef.current = false;
            setGeneratingCopy(false);
        }
    }, [canGenerateCopy, generateCopyPayload, token]);

    const uploadImage = useCallback(async (savedId, file) => {
        if (!file) {
            return null;
        }

        setUploadingImage(true);
        try {
            const presign = await presignPackageImage(token, savedId, {
                originalName: file.name,
                contentType: file.type,
                size: file.size,
            });
            if (presign?.type !== "success") {
                return presign;
            }

            const putResponse = await fetch(presign.uploadUrl, {
                method: "PUT",
                headers: { "Content-Type": file.type },
                body: file,
            });
            if (!putResponse.ok) {
                return { type: "error", message: "Erro no upload da imagem." };
            }

            return await confirmPackageImage(token, savedId, {
                key: presign.key,
                originalName: file.name,
                contentType: file.type,
            });
        } catch (error) {
            return { type: "error", message: "Erro no upload da imagem." };
        } finally {
            setUploadingImage(false);
        }
    }, [token]);

    const save = useCallback(async () => {
        setSaving(true);
        setError("");
        try {
            const payload = buildPayload(form);
            if (payload?.type === "error") {
                setError(payload.message);
                return payload;
            }

            const id = productPackage?._id || productPackage?.id;
            const saved = id
                ? await updateProductPackage(token, id, payload)
                : await createProductPackage(token, payload);

            const imageOnlyEdit = id && imageFile && saved?.message === "Nothing changed";
            if (saved?.type !== "success" && !imageOnlyEdit) {
                setError(saved?.message || "Erro ao salvar pacote.");
                return saved;
            }

            let result = imageOnlyEdit ? { type: "success", data: productPackage } : saved;
            const savedPackageId = result?.data?._id || id;
            if (imageFile && savedPackageId) {
                result = await uploadImage(savedPackageId, imageFile);
                if (result?.type !== "success") {
                    setError(result?.message || "Erro ao enviar imagem.");
                    return result;
                }
            }

            return result;
        } catch (error) {
            const result = { type: "error", message: "Erro ao salvar pacote." };
            setError(result.message);
            return result;
        } finally {
            setSaving(false);
        }
    }, [form, imageFile, productPackage, token, uploadImage]);

    return {
        form,
        quote,
        savings,
        canGenerateCopy,
        imageFile,
        referenceLoading,
        generatingCopy,
        saving,
        uploadingImage,
        busy: saving || generatingCopy || uploadingImage,
        error,
        copyStatus,
        canAddItem: (form.items || []).length < MAX_PACKAGE_ITEMS,
        canRemoveItem: (form.items || []).length > 1,
        updateField,
        updateItem,
        addItem,
        removeItem,
        setImageFile,
        generateCopy,
        save,
        buildPayload: () => buildPayload(form),
    };
};
